import {
  IconArrowLeft,
  Interfaces,
  Layout,
  Link,
  LocationSearchInput,
  Theme,
  Container,
  LocationContact
} from "@life-without-barriers/react-components"
import { ContactUsLocation, LocationPageParameters } from "../../contentTypes"
import { helpers } from "@life-without-barriers/utilities"
import styled, { ThemeProps, withTheme } from "styled-components"

import PlacesApiLoader from "@life-without-barriers/react-components/src/PlacesApiLoader"
import React from "react"
import { graphql } from "gatsby"
import selectLocation from "../../selectLocation"

const { corporateThemeFull } = Theme

const SubHeading = styled.h2`
  font-size: 19px;
  font-weight: 700;
`

export interface LocationResultsParams {
  searchParams: LocationPageParameters
  currentLocation: ContactUsLocation
  orderedNearby: ContactUsLocation[]
}

interface BreadcrumbLinkProps {
  children: React.ReactNode
  title: string
  to: string
}

const toOpeningHours = (
  openingHours: string
): { mondayToFriday: string; saturday: string; sunday: string } | undefined => {
  switch (openingHours) {
    case "8:30AM - 5PM M-F":
      return {
        mondayToFriday: "8:30AM - 5PM",
        saturday: "Closed",
        sunday: "Closed"
      }
    case "9AM - 4:30PM M-F":
      return {
        mondayToFriday: "9AM - 4:30PM",
        saturday: "Closed",
        sunday: "Closed"
      }
    case "9AM - 5PM M-F":
      return {
        mondayToFriday: "9AM - 5PM",
        saturday: "Closed",
        sunday: "Closed"
      }
    case "Do not show":
      return {
        mondayToFriday: "9AM - 5PM",
        saturday: "Closed",
        sunday: "Closed"
      }
    default:
      throw new Error(`Unknown opening hours ${openingHours}`)
  }
}

const BreadcrumbLink = withTheme(
  ({
    children,
    title,
    to,
    theme
  }: BreadcrumbLinkProps & ThemeProps<Interfaces.Theme>) => (
    <div className="pt4">
      <span className="ph2">
        <IconArrowLeft height="10" width="6" color={theme.dark} />
      </span>
      <Link
        role="navigation"
        className="black hover-black underline fw5"
        to={to}
        title={title}
      >
        {children}
      </Link>
    </div>
  )
)

const LocationResults = ({
  searchParams,
  currentLocation,
  orderedNearby
}: LocationResultsParams) => {
  return (
    <Layout theme={corporateThemeFull}>
      <div className="ph3 ph4-ns bg-lwb-grey-xxx-light pv5-ns pv4">
        <Container>
          <div className="tc">
            <h1 className="fw8">Find a location near you</h1>
            <PlacesApiLoader>
              <LocationSearchInput
                className="pt4 pb2"
                onSelect={selectLocation}
                onSeachButtonClick={helpers.noop}
                onChange={helpers.noop}
                enabled={true}
                initialLocation={searchParams.enteredAddress}
                onSeachInputFocus={helpers.noop}
              />
            </PlacesApiLoader>
            <BreadcrumbLink title="Contact us" to="/contact-us/">
              <span>Contact us</span>
            </BreadcrumbLink>
          </div>
        </Container>
      </div>
      <div className="ph3 ph4-ns bg-lwb-theme-medium pv5-ns pv4">
        {searchParams.address && (
          <Container>
            <div className="w-100-l">
              <SubHeading className="white pb3 pb4-ns">
                Nearest location to {searchParams.address}
              </SubHeading>
              <LocationContact
                phoneNumber={currentLocation.phoneNumber}
                address={{
                  streetAddress: currentLocation.streetAddress,
                  postcode: currentLocation.postcode,
                  state: currentLocation.state,
                  suburb: currentLocation.town
                }}
                location={currentLocation.location}
                emailAddress={currentLocation.emailAddress}
                notes={
                  currentLocation.description
                    ? {
                        kind: "html",
                        html: currentLocation.description.childMarkdownRemark
                          .html
                      }
                    : undefined
                }
                openingHours={toOpeningHours(currentLocation.openingHours)}
                googleApiKey={process.env.GATSBY_GOOGLE_API_KEY ?? ""}
              />
            </div>
          </Container>
        )}
      </div>
      {searchParams.address && (
        <div className="ph3 ph4-ns pv5-ns pv4">
          <Container>
            <div className="w-100-l">
              <SubHeading className="pb3 pb4-ns">
                We also found these locations
              </SubHeading>
              {orderedNearby.map(
                (nearby) =>
                  nearby && (
                    <div className="pb4" key={nearby.id}>
                      <LocationContact
                        phoneNumber={nearby.phoneNumber}
                        address={{
                          streetAddress: nearby.streetAddress,
                          postcode: nearby.postcode,
                          state: nearby.state,
                          suburb: nearby.town
                        }}
                        location={nearby.location}
                        emailAddress={nearby.emailAddress}
                        notes={
                          nearby.description
                            ? {
                                kind: "html",
                                html: nearby.description.childMarkdownRemark
                                  .html
                              }
                            : undefined
                        }
                        openingHours={toOpeningHours(nearby.openingHours)}
                        googleApiKey={process.env.GATSBY_GOOGLE_API_KEY ?? ""}
                      />
                    </div>
                  )
              )}
            </div>
          </Container>
        </div>
      )}
    </Layout>
  )
}

export const contactUsLocationFragment = graphql`
  fragment contactUsLocationFragment on ContentfulContactUsLocation {
    id
    streetAddress
    town
    state
    postcode
    openingHours
    location {
      lat
      lon
    }
    description {
      childMarkdownRemark {
        html
      }
    }
    phoneNumber
    emailAddress
  }
`

export default LocationResults
